import { Store } from 'Application/Store/Store';
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine';
import { meshSelectionStore } from 'Domain/Mesh/State/MeshSelectionStore';
import { toogleAnimationVisible, setKeyframeToBegin, setKeyframeToEnd, setVersions } from 'Domain/Scene/Manager/SceneManager.js';
import { SET_MODIFICATIONS } from 'Application/Store/Actions/ViewportTypes';
import { getTimes } from 'Domain/Scene/Handler/AnimationHandler';
import { nextRenderTick, delay } from 'Domain/Scene/Manager/InitializationOrchestrator';
import { GetAllVersions, GetReferencesModifications } from 'Domain/Scene/Builder/GetReferencesModifications';
import { getUserName } from 'Ui/Header';
import { ihm_viewer__dictionary, locale } from 'Application/i18n/i18nViewer';
import { hasAnyToothBeenModified } from './ToothTransformHandler';
import { applyModification } from './ModificationApplier';

let maxiMatrixHandler = [];
let mandiExtraHandler = [];
let sessionMovementHistory = {};
let globalTeethObjects = [];

export const setSaveHandlerData = (maxi, mandi, teethObjects) => {
    maxiMatrixHandler = maxi;
    mandiExtraHandler = mandi;
    globalTeethObjects = teethObjects;
};

/* ──────────────────────────────────────────────
   SESSION MOVEMENT HISTORY
   ────────────────────────────────────────────── */

let movementHistoryStack = [];
let movementRedoStack = [];

export const clearSessionMovementHistory = () => {
    sessionMovementHistory = {};
    movementHistoryStack = [];
    movementRedoStack = [];
};

export const addToSessionMovementHistory = (toothId, type, value) => {
    if (!sessionMovementHistory[toothId]) sessionMovementHistory[toothId] = {};
    sessionMovementHistory[toothId][type] = (sessionMovementHistory[toothId][type] || 0) + value;
    movementHistoryStack.push({ toothId, type, value });
    movementRedoStack = [];
};

export const undoLastToothAction = (toothId) => {
    for (let i = movementHistoryStack.length - 1; i >= 0; i--) {
        if (movementHistoryStack[i].toothId === toothId) {
            const [action] = movementHistoryStack.splice(i, 1);
            movementRedoStack.push(action);
            sessionMovementHistory[action.toothId][action.type] -= action.value;
            if (Math.abs(sessionMovementHistory[action.toothId][action.type]) < 0.001) {
                delete sessionMovementHistory[action.toothId][action.type];
            }
            if (Object.keys(sessionMovementHistory[action.toothId]).length === 0) {
                delete sessionMovementHistory[action.toothId];
            }
            return;
        }
    }
};

export const redoLastToothAction = (toothId) => {
    for (let i = movementRedoStack.length - 1; i >= 0; i--) {
        if (movementRedoStack[i].toothId === toothId) {
            const [action] = movementRedoStack.splice(i, 1);
            movementHistoryStack.push(action);
            if (!sessionMovementHistory[action.toothId]) sessionMovementHistory[action.toothId] = {};
            sessionMovementHistory[action.toothId][action.type] = (sessionMovementHistory[action.toothId][action.type] || 0) + action.value;
            return;
        }
    }
};

export const resetToothHistory = (toothId) => {
    movementHistoryStack = movementHistoryStack.filter(a => a.toothId !== toothId);
    movementRedoStack = movementRedoStack.filter(a => a.toothId !== toothId);
    delete sessionMovementHistory[toothId];
};

const collectMovementHistory = () => {
    const cleaned = {};
    Object.entries(sessionMovementHistory).forEach(([toothId, movements]) => {
        const nonZero = {};
        Object.entries(movements).forEach(([type, value]) => {
            if (Math.abs(value) > 0.001) {
                nonZero[type] = value;
            }
        });
        if (Object.keys(nonZero).length > 0) {
            cleaned[toothId] = nonZero;
        }
    });
    return cleaned;
};

/* ──────────────────────────────────────────────
   SAVE MODIFICATIONS
   ────────────────────────────────────────────── */

export const saveModifications = async () => {
    if (!hasAnyToothBeenModified(globalTeethObjects)) return;

    Store.dispatch(toogleAnimationVisible('none'));
    await nextRenderTick();

    const { jawModif, jawOrthup } = await collectAllTeethMatrices();
    const movementHistory = collectMovementHistory();
    const saveData = await buildSaveData(jawModif, jawOrthup, movementHistory);
    await sendModificationsToServer(saveData);
};

/* ──────────────────────────────────────────────
   DATA COLLECTION
   ────────────────────────────────────────────── */

const collectAllTeethMatrices = async () => {
    const wasMandiAdjustActive = Store.getState().ViewportReducer.mandiAdjustActive;
    const tempMandiGroup = prepareMandiGroupForCollection(wasMandiAdjustActive);

    const jawModif = { mandi: {}, maxi: {} };
    const jawOrthup = { mandi: {}, maxi: {} };

    try {
        await collectToothDataForSave(maxiMatrixHandler, jawModif.maxi, jawOrthup.maxi);
        await collectToothDataForSave(mandiExtraHandler, jawModif.mandi, jawOrthup.mandi);
    } finally {
        restoreMandiGroupAfterCollection(wasMandiAdjustActive, tempMandiGroup);
    }

    return { jawModif, jawOrthup };
};

export const collectToothDataForSave = (handler, jawModif, jawOrthup) => {
    Object.values(handler).forEach(toothData => {
        collectOrthupMatrix(toothData, jawOrthup);
        collectModificationMatrix(toothData, jawModif);
    });
};

const collectOrthupMatrix = (toothData, jawOrthup) => {
    toothData.updateWorldPosition();
    toothData.matrixCalculatorObj.updateMatrixWorld(true);
    jawOrthup[toothData.refObj.userData.toothID] = toothData.matrixCalculatorObj.matrixWorld.clone();
};

const collectModificationMatrix = (toothData, jawModif) => {
    ensurePivotIsPlaced(toothData);
    jawModif[toothData.refObj.userData.toothID] = toothData.pivot.matrix.clone();
};

const ensurePivotIsPlaced = (toothData) => {
    if (toothData.pivotAlreadyPlaced) return;
    toothData.pivot.add(toothData.refObj);
    toothData.placePivot();
    toothData.pivot.updateMatrix();
    toothData.pivotAlreadyPlaced = true;
};

/* ──────────────────────────────────────────────
   MANDI GROUP HELPERS (for collection)
   ────────────────────────────────────────────── */

const prepareMandiGroupForCollection = (wasMandiAdjustActive) => {
    if (!wasMandiAdjustActive) return null;
    const mandiGroupGlobal = threeEngine.getMandiGroup();
    if (!mandiGroupGlobal) return null;
    const tempGroup = mandiGroupGlobal.clone(true);
    tempGroup.name = "mandiGroupTemp";
    const scene = threeEngine.getScene();
    if (scene) scene.add(tempGroup);
    mandiGroupGlobal.visible = false;
    mandiGroupGlobal.position.set(0, 0, 0);
    return tempGroup;
};

const restoreMandiGroupAfterCollection = (wasMandiAdjustActive, tempGroup) => {
    if (!wasMandiAdjustActive) return;
    const mandiAdjustVector = Store.getState().ViewportReducer.mandiAdjust;
    threeEngine.getMandiGroup().position.set(mandiAdjustVector.x, mandiAdjustVector.y, mandiAdjustVector.z);
    const scene = threeEngine.getScene();
    if (scene) scene.remove(tempGroup);
    threeEngine.getMandiGroup().visible = true;
};

/* ──────────────────────────────────────────────
   MALOC / SETUP VERSION DATA
   ────────────────────────────────────────────── */

const KEYFRAME_TRANSITION_DELAY = 100;

export const collectMalocAndSetupVersionsData = async () => {
    const malocData = collectJawDataAtKeyframe(setKeyframeToBegin);
    Store.dispatch(setKeyframeToEnd(getTimes()));
    await delay(KEYFRAME_TRANSITION_DELAY);
    const setupData = collectJawDataAtCurrentFrame();
    Store.dispatch(setKeyframeToBegin());
    return { malocData, setupData };
};

const collectJawDataAtKeyframe = (keyframeDispatcher) => {
    Store.dispatch(keyframeDispatcher());
    return collectJawDataAtCurrentFrame();
};

const collectJawDataAtCurrentFrame = () => {
    const jawModif = { mandi: {}, maxi: {} };
    const emptyOrthup = { mandi: {}, maxi: {} };
    collectToothDataForSave(maxiMatrixHandler, jawModif.maxi, emptyOrthup);
    collectToothDataForSave(mandiExtraHandler, jawModif.mandi, emptyOrthup);
    return jawModif;
};

/* ──────────────────────────────────────────────
   SERVER COMMUNICATION
   ────────────────────────────────────────────── */

const buildSaveData = async (jawModif, jawOrthup, movementHistory) => {
    const author = await getUserName() || 'unknown';
    const email = await fetchCurrentUserEmail();
    const parentVersion = Store.getState().ViewportReducer.selectedVersion;

    return {
        id: threeEngine.getProjectId(),
        modifications: jawModif,
        priority: 1,
        author,
        lastUpdate: new Date().toISOString(),
        orthupmatrix: jawOrthup,
        email,
        parentVersion,
        movementHistory
    };
};

const fetchCurrentUserEmail = async () => {
    try {
        const response = await fetch(process.env.REACT_APP_U1_VIEWERSVC_GETUSER_URL + '/getemail', {
            credentials: 'include'
        });
        const data = await response.json();
        return data.email || 'unknown';
    } catch (error) {
        console.error('Error fetching user email:', error);
        return 'unknown';
    }
};

const sendModificationsToServer = async (saveData) => {
    try {
        await fetch(process.env.REACT_APP_U1_VIEWERSVC_API + '/project/save-matrix', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(saveData)
        });
        await handleSaveSuccess();
    } catch (error) {
        console.error(error);
    }
};

const handleSaveSuccess = async () => {
    await refreshModifications();
    const currentModifications = meshSelectionStore.getModifications();
    const context = { maxiMatrixHandler, mandiExtraHandler, globalTeethObjects };
    await applyModification(currentModifications.length - 1, false, context);
};

/* ──────────────────────────────────────────────
   VERSION REFRESH
   ────────────────────────────────────────────── */

export const refreshModifications = async () => {
    const allVersions = await GetAllVersions();
    const updatedVersions = buildVersionsDropdownOptions(allVersions);
    Store.dispatch(setVersions(updatedVersions));
    const modifications = await GetReferencesModifications();
    meshSelectionStore.setModifications(modifications);
    Store.dispatch({ type: SET_MODIFICATIONS, payload: modifications });
};

const buildVersionsDropdownOptions = (allVersions) => {
    const options = [createDisabledHeaderOption(), createOriginalVersionOption()];
    allVersions.forEach((version, slotIndex) => {
        if (isValidVersion(version)) {
            options.push(createVersionOption(slotIndex + 1, slotIndex));
        }
    });
    return options;
};

const createDisabledHeaderOption = () => ({
    label: ihm_viewer__dictionary.VERSION_MODE[locale()],
    value: '',
    disabled: true
});

const createOriginalVersionOption = () => ({
    label: ihm_viewer__dictionary.ORIGINAL_VERSION[locale()],
    value: -1,
    disabled: false
});

const isValidVersion = (version) => version !== -2;

const createVersionOption = (displayNumber, value) => ({
    label: `${ihm_viewer__dictionary.VERSION_LABEL[locale()]} ${displayNumber}`,
    value,
    disabled: false
});

/* ──────────────────────────────────────────────
   REMOVE MODIFICATION
   ────────────────────────────────────────────── */

export const removeModification = async () => {
    try {
        const modificationId = await getModificationIdToRemove();
        await deleteModificationFromServer(modificationId);
        await refreshModifications();
        const context = { maxiMatrixHandler, mandiExtraHandler, globalTeethObjects };
        await applyModification(-1, false, context);
    } catch (error) {
        console.error('Erreur lors de la suppression de la modification:', error);
    }
};

const getModificationIdToRemove = async () => {
    const selectedSlot = Store.getState().ViewportReducer.selectedVersion;
    const allVersionSlots = await GetAllVersions();
    const modificationId = allVersionSlots[selectedSlot];
    if (modificationId === undefined || modificationId === -2) {
        throw new Error(`Invalid version slot for deletion: ${selectedSlot}`);
    }
    return modificationId;
};

const deleteModificationFromServer = async (modificationId) => {
    const response = await fetch(process.env.REACT_APP_U1_VIEWERSVC_API + '/project/remove-matrix', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modificationId, projectId: threeEngine.getProjectId() })
    });
    if (!response.ok) {
        throw new Error(`Erreur lors de la suppression: ${response.statusText}`);
    }
};
